<template>
  <v-container fluid class="pa-0 pa-md-5">
    <v-row class="mb-2">
      <v-col cols="12" sm="6" md="4">
        <v-card dark class="pa-4 rounded-lg" outlined elevation="2" style="border-left: 5px solid #1976d2">
          <div class="text-overline mb-1 grey--text text--darken-1">Offene Verträge</div>
          <div class="d-flex align-center">
            <div class="text-h4 primary--text font-weight-bold mr-2">{{ totalContractsCount }}</div>
            <v-chip x-small color="primary" outlined v-if="loading">lade...</v-chip>
          </div>
          <div class="mt-2 text-caption grey--text text--lighten-1">
            <div>
              <strong class="white--text">Heute:</strong> {{ stats.today.total }}
              <span v-if="stats.today.online > 0" class="orange--text text--lighten-2">({{ stats.today.online }} Online)</span>
            </div>
            <div>
              <strong class="white--text">Gestern:</strong> {{ stats.yesterday.total }}
              <span v-if="stats.yesterday.online > 0" class="orange--text text--lighten-2">({{ stats.yesterday.online }} Online)</span>
            </div>
          </div>
          <v-icon style="position: absolute; right: 15px; top: 15px; opacity: 0.1; font-size: 60px">mdi-file-document-multiple</v-icon>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="4" v-if="!showRecentlyAddedContracts">
        <v-card dark class="pa-4 rounded-lg" outlined elevation="2" style="border-left: 5px solid #4caf50">
          <div class="text-overline mb-1 grey--text text--darken-1">Online Abschlüsse (Gesamt)</div>
          <div class="text-h4 success--text font-weight-bold">{{ totalOnlineContractsCount }}</div>

          <div class="mt-2 text-caption grey--text text--lighten-1">
            <div><strong class="white--text">Heute:</strong> {{ stats.today.online }}</div>
            <div><strong class="white--text">Gestern:</strong> {{ stats.yesterday.online }}</div>
          </div>

          <v-icon style="position: absolute; right: 15px; top: 15px; opacity: 0.1; font-size: 60px">mdi-web</v-icon>
        </v-card>
      </v-col>
    </v-row>

    <v-alert v-if="showNewContractAlert" type="success" dismissible border="left" elevation="2" class="mb-5">
      <v-row align="center">
        <v-col class="grow">Ein neuer Vertrag wurde erstellt!</v-col>
        <v-col class="shrink">
          <v-btn color="white" outlined small @click="reloadAll">Neu laden</v-btn>
        </v-col>
      </v-row>
    </v-alert>

    <v-alert v-if="!userService.hasRight('c_open_contracts')" type="info" text dense class="mb-5">
      Die Verträge in dieser Liste sind noch in Bearbeitung / noch nicht im TAC hinterlegt
    </v-alert>

    <v-card dark elevation="3" class="mb-6 rounded-lg">
      <v-card-text class="pa-4">
        <v-row align="center" dense>
          <v-col cols="12" md="auto" class="d-flex justify-center justify-md-start mb-2 mb-md-0">
            <v-btn-toggle v-model="viewMode" mandatory dense color="primary" rounded>
              <v-btn :value="'open'" small> <v-icon left small>mdi-inbox</v-icon> Offen </v-btn>
              <v-btn :value="'recent'" small> <v-icon left small>mdi-history</v-icon> Zuletzt </v-btn>
            </v-btn-toggle>
          </v-col>
          <v-divider vertical class="mx-4 hidden-sm-and-down"></v-divider>

          <v-col v-if="true || userService.hasRight('c_create') || userService.hasRight('c_open_contracts')" cols="12" sm="6" md="3">
            <v-select
              :items="studios"
              label="Studios filtern"
              v-model="studioSelect"
              item-text="name"
              item-value="_id"
              multiple
              outlined
              dense
              hide-details
              small-chips
              deletable-chips>
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0" class="grey--text text-caption font-weight-bold"> {{ studioSelect.length }} Studios </span>
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="searchQuery"
              label="Suchen (Name, E-Mail)"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
              @input="debounceSearch"
              placeholder="Eingeben..."></v-text-field>
          </v-col>
          <v-col cols="12" md="auto" class="d-flex justify-end align-center ml-auto">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  @click="sortByStartDate = !sortByStartDate"
                  v-bind="attrs"
                  v-on="on"
                  :color="sortByStartDate ? 'orange' : 'grey darken-1'"
                  class="mr-2">
                  <v-icon>mdi-calendar-clock</v-icon>
                </v-btn>
              </template>
              <span>{{ sortByStartDate ? "Sortiert nach Vertragsstart" : "Sortiert nach Erstellung" }}</span>
            </v-tooltip>
            <v-tooltip v-if="userService.hasRight('c_create') || userService.hasRight('c_open_contracts')" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="grey darken-1" @click="selectAllStudios" v-bind="attrs" v-on="on">
                  <v-icon>{{
                    studioSelect.length === studios.length ? "mdi-checkbox-multiple-outline" : "mdi-checkbox-multiple-blank-outline"
                  }}</v-icon>
                </v-btn>
              </template>
              <span>Alle Studios an/abwählen</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon @click="toggleLoadAll" v-bind="attrs" v-on="on" :color="isShowingAll ? 'primary' : 'grey darken-1'" class="mr-2">
                  <v-icon>{{ isShowingAll ? "mdi-all-inclusive" : "mdi-format-list-bulleted" }}</v-icon>
                </v-btn>
              </template>
              <span>{{ isShowingAll ? "Wieder paginationieren" : "Alle Verträge laden" }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="primary" @click="reloadAll" v-bind="attrs" v-on="on" :loading="loading">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Liste aktualisieren</span>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-expand-transition>
          <div v-if="showRecentlyAddedContracts" class="mt-4 pt-4 border-top">
            <v-row dense align="center" justify="center">
              <v-col cols="12" sm="5">
                <v-text-field v-model="recentlyContractsStartDate" label="Startdatum" type="date" outlined dense hide-details></v-text-field>
              </v-col>
              <v-col cols="12" sm="5">
                <v-text-field v-model="recentlyContractsEndDate" label="Enddatum" type="date" outlined dense hide-details></v-text-field>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>
      </v-card-text>
    </v-card>

    <v-card v-if="!showRecentlyAddedContracts" dark elevation="0" class="mx-auto transparent" color="transparent">
      <v-row v-if="loading && page === 1" class="justify-center py-10">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      </v-row>

      <v-row v-else-if="openContracts.length === 0" class="justify-center py-10">
        <v-col cols="12" class="text-center grey--text text--lighten-1">
          <v-icon size="80" color="grey darken-2">mdi-inbox-outline</v-icon>
          <div class="text-h6 mt-4">Keine offenen Verträge gefunden</div>
          <div class="subtitle-2 grey--text text--darken-1">Versuche die Filtereinstellungen anzupassen</div>
        </v-col>
      </v-row>

      <div v-else>
        <v-expansion-panels v-model="userContractPanel" dark>
          <v-expansion-panel v-for="(user, i) of sortedOpenContracts" :key="user._id" class="mb-3 rounded-lg elevation-4">
            <v-expansion-panel-header :color="'#424242'">
              <template v-slot:default="{ open }">
                <v-row no-gutters align="center">
                  <v-col cols="12" sm="4" class="d-flex align-center">
                    <v-avatar size="32" :color="hasPublicContract(user) ? 'orange' : 'rgba(255, 255, 255, 0.1)'" class="mr-3">
                      <span class="white--text text-caption">{{ i + 1 }}</span>
                    </v-avatar>
                    <span class="text-h6 font-weight-bold mr-2">{{ user.vorname }} {{ user.nachname }}</span>
                  </v-col>
                  <v-col cols="6" sm="3" class="text-caption grey--text text--lighten-2 d-flex align-center">
                    <v-icon x-small left color="grey lighten-2">mdi-map-marker</v-icon>
                    <span class="text-truncate">{{ getStudiosOfContracts(user.contracts) }}</span>
                  </v-col>
                  <v-col cols="6" sm="3" class="text-caption white--text d-flex align-center justify-end justify-sm-center">
                    <v-chip x-small label outlined class="mr-1" :color="sortByStartDate ? 'orange' : 'grey'">
                      Start: {{ $moment(user.newestStartDate).format("DD.MM.YY") }}
                    </v-chip>
                  </v-col>
                  <v-col cols="12" sm="2" class="text-right text-caption grey--text">
                    Erstellt: {{ $moment(user.newestCreatedAt).format("DD.MM HH:mm") }}
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="grey darken-4 pt-4">
              <v-expansion-panels v-model="openContractPanel" accordion flat>
                <v-expansion-panel
                  v-for="(contract, j) of user.contracts"
                  :key="contract._id"
                  class="transparent"
                  :disabled="contract.status.toString() !== 'created'">
                  <v-expansion-panel-header class="pl-0 py-2">
                    <div class="d-flex align-center" style="width: 100%">
                      <v-icon small class="mr-3" color="primary">mdi-file-document-outline</v-icon>

                      <div class="d-flex flex-column mr-auto">
                        <span class="subtitle-1 font-weight-medium">{{ getNameOfContract(contract.contractId) }}</span>
                        <span class="caption grey--text text--lighten-1">
                          Erstellt: {{ $moment(contract.createdAt).format("DD.MM.YY HH:mm") }} Uhr
                        </span>
                      </div>

                      <div class="d-flex align-center">
                        <v-chip x-small class="mr-2" v-if="contract.publicContract" color="orange" label outlined>Online</v-chip>
                        <span class="text-caption grey--text">Start: {{ $moment(contract.startDate).format("DD.MM.YYYY") }}</span>
                      </div>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content>
                    <v-row dense class="mb-2">
                      <v-col v-if="userService.hasRight('c_open_contracts') && contract.editingBy" cols="12">
                        <v-alert v-if="contract.editingBy.toString() === userId.toString()" type="info" dense outlined border="left" colored-border>
                          <v-row align="center" no-gutters>
                            <v-col class="grow">Du bearbeitest diesen Vertrag.</v-col>
                            <v-col class="shrink">
                              <v-btn small color="error" text @click="takeEditAction(false, contract.index, user._id)">Bearbeitung beenden</v-btn>
                            </v-col>
                          </v-row>
                        </v-alert>
                        <v-alert v-else type="warning" dense outlined>Vertrag wird gerade bearbeitet.</v-alert>
                      </v-col>
                      <v-col v-else cols="12">
                        <v-btn
                          v-if="userService.hasRight('c_open_contracts')"
                          small
                          color="primary"
                          outlined
                          @click="takeEditAction(true, contract.index, user._id)">
                          <v-icon left small>mdi-pencil</v-icon> Bearbeitung übernehmen
                        </v-btn>
                      </v-col>
                    </v-row>

                    <v-row dense>
                      <v-col cols="12" md="6">
                        <v-card outlined color="grey darken-3" class="pa-3 fill-height">
                          <div class="subtitle-2 grey--text mb-2">Details</div>
                          <div v-if="contract.customer.contractCreatorInfos" class="mb-2">
                            <v-icon x-small left>mdi-account-edit</v-icon>
                            <span class="caption"
                              >Erstellt von: {{ contract.customer.contractCreatorInfos.firstName }}
                              {{ contract.customer.contractCreatorInfos.lastName }}</span
                            >
                          </div>
                          <div v-if="contract.category.isTakeover && contract.category.takeover" class="mb-2 red--text text--lighten-2">
                            <v-icon x-small left color="red lighten-2">mdi-account-switch</v-icon>
                            <span class="caption font-weight-bold"
                              >Übernahme von: {{ contract.category.takeover.firstName }} {{ contract.category.takeover.lastName }}</span
                            >

                            <v-img style="background-color: white" max-width="200px" :src="contract.category.takeover.signature"></v-img>
                          </div>
                          <div v-if="contract.category.isAffiliate && contract.category.affiliateInfos" class="mb-2 green--text text--lighten-2">
                            <v-icon x-small left color="green lighten-2">mdi-account-heart</v-icon>
                            <span class="caption"
                              >Empfohlen von: {{ contract.category.affiliateInfos.firstName }} {{ contract.category.affiliateInfos.lastName }}</span
                            >
                          </div>
                          <div v-if="contract.hasPurchasedPersonalTraining" class="mb-2">
                            <v-chip x-small color="blue" label>Personal Training</v-chip>
                          </div>
                          <div v-if="hasImages(contract)" class="mt-3">
                            <div class="caption grey--text mb-1">Anhänge:</div>
                            <div class="d-flex flex-wrap">
                              <template v-for="(img, idx) in getAllImages(contract)">
                                <v-btn :key="idx" large color="primary" class="mr-2 mb-2" @click="downloadContractImageWrapper(img)">
                                  <v-icon left x-small>mdi-paperclip</v-icon> Bild {{ idx + 1 }}
                                </v-btn>
                              </template>
                            </div>
                          </div>
                        </v-card>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-card outlined color="grey darken-3" class="pa-3 fill-height">
                          <div class="subtitle-2 grey--text mb-2">Verlauf & Prüfung</div>
                          <div class="overflow-y-auto mb-3" style="max-height: 100px">
                            <div v-for="(comment, cIdx) of contract.comments" :key="cIdx" class="caption mb-1">
                              <span class="grey--text">{{ $moment(comment.date).format("DD.MM HH:mm") }}:</span>
                              <span v-if="comment.status" class="font-weight-bold">{{ comment.status }}</span>
                              <span v-if="comment.comment"> - {{ comment.comment }}</span>
                            </div>
                          </div>
                          <v-select
                            v-if="userService.hasRight('c_open_contracts')"
                            v-model="contract.commentStatus"
                            :items="reasons"
                            label="Grund"
                            dense
                            outlined
                            hide-details
                            class="mb-2"
                            background-color="grey darken-4"></v-select>
                          <v-textarea
                            v-model="contract.comment"
                            rows="2"
                            label="Kommentar"
                            dense
                            outlined
                            hide-details
                            class="mb-2"
                            background-color="grey darken-4"></v-textarea>
                          <div class="d-flex justify-end">
                            <v-btn
                              @click="addComment(user._id, contract.index, contract.comment, contract.commentStatus)"
                              :disabled="!contract.comment && !contract.commentStatus"
                              >Speichern</v-btn
                            >
                          </div>
                        </v-card>
                      </v-col>
                    </v-row>

                    <v-divider class="my-3"></v-divider>
                    <v-row align="center">
                      <v-col cols="12" md="8" class="d-flex flex-wrap">
                        <template v-if="userService.hasRight('c_open_contracts')">
                          <v-btn
                            color="success"
                            class="mr-2 mb-2"
                            @click="
                              updateContract(
                                user._id,
                                contract.index,
                                contract.comment,
                                contract.commentStatus,
                                'accepted',
                                contract.contractId,
                                contract.studio._id,
                                contract.externallySaved,
                                contract.affiliateSaved,
                              )
                            ">
                            <v-icon left>mdi-check</v-icon> Bestätigen
                          </v-btn>
                          <v-btn
                            color="error"
                            class="mr-2 mb-2"
                            @click="
                              updateContract(
                                user._id,
                                contract.index,
                                contract.comment,
                                contract.commentStatus,
                                'rejected',
                                contract.contractId,
                                contract.studio._id,
                                contract.externallySaved,
                                contract.affiliateSaved,
                              )
                            ">
                            <v-icon left>mdi-close</v-icon> Zurückweisen
                          </v-btn>
                          <v-btn v-if="needsTacCopy(contract)" color="orange darken-3" class="mr-2 mb-2" @click="copyToTAC(contract)">
                            <v-icon left>mdi-content-copy</v-icon> TAC
                          </v-btn>
                        </template>
                      </v-col>
                      <v-col cols="12" md="4" class="d-flex justify-end">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn icon color="green lighten-2" @click="openPaymentDialog(user, contract)" v-bind="attrs" v-on="on" class="mr-2">
                              <v-icon>mdi-credit-card-settings-outline</v-icon>
                            </v-btn>
                          </template>
                          <span>Zahlungsart bearbeiten</span>
                        </v-tooltip>
                        <v-btn
                          v-if="userService.hasRight('c_create')"
                          icon
                          color="red lighten-2"
                          @click="visibleDeleteDialogIndex = user._id + '-' + contract.index"
                          ><v-icon>mdi-delete</v-icon></v-btn
                        >
                        <v-btn icon color="grey lighten-1" @click="$router.push('/dashboard/contract/view/' + user._id + '/' + contract.index)"
                          ><v-icon>mdi-eye</v-icon></v-btn
                        >
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              color="blue lighten-2"
                              @click="printUserContract(user._id, contract.index, getNameOfContract(contract.contractId))"
                              v-bind="attrs"
                              v-on="on"
                              ><v-icon>mdi-printer</v-icon></v-btn
                            >
                          </template>
                          <span>Drucken / PDF</span>
                        </v-tooltip>
                      </v-col>
                    </v-row>

                    <v-expand-transition>
                      <div v-if="openContractPanel === j" class="mt-3">
                        <object
                          v-if="!isApp && contractSauce"
                          width="100%"
                          height="400px"
                          :data="contractSauce"
                          class="rounded border-white"></object>
                        <v-btn
                          v-else
                          color="primary"
                          block
                          @click="printUserContract(user._id, contract.index, getNameOfContract(contract.contractId))"
                          >PDF Öffnen</v-btn
                        >
                      </div>
                    </v-expand-transition>
                    <delete-dialog
                      @onDelete="deleteContract(user._id, contract.index)"
                      @onCancel="visibleDeleteDialogIndex = null"
                      :dialog-title="'Vertrag löschen'"
                      :dialog-visible="visibleDeleteDialogIndex === user._id + '-' + contract.index"
                      :itemName="'Vertrag'"></delete-dialog>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="text-center mt-6" v-if="page < totalPages">
          <v-btn :loading="loadingMore" @click="loadMore" color="primary" rounded outlined large>
            <v-icon left>mdi-chevron-down</v-icon> Mehr laden ({{ openContracts.length }} / {{ totalContractsCount }})
          </v-btn>
        </div>
      </div>
    </v-card>

    <v-card v-if="showRecentlyAddedContracts" class="mt-5 rounded-lg">
      <v-data-table
        dark
        :headers="recentHeaders"
        :items="recentlyAcceptedContracts"
        :loading="recentlyContractsLoading"
        no-data-text="Keine Verträge im Zeitraum gefunden">
        <template v-slot:item.nr="{ index }">{{ index + 1 }}</template>
        <template v-slot:item.studio="{ item }">{{ getNameOfStudio(item.studio) }}</template>
        <template v-slot:item.contract="{ item }">{{
          item.latestAcceptedContractTitle.includes("Mitglied") ? "MGV" : item.latestAcceptedContractTitle
        }}</template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon small @click="pushToUser(item._id)"><v-icon>mdi-eye-outline</v-icon></v-btn>
          <v-btn icon small @click="printUserContract(item.userId, item.index, item.name)"><v-icon>mdi-file-pdf-box</v-icon></v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-row>
      <v-col cols="12">
        <open-nutrition-sales :studios="studioSelect"></open-nutrition-sales>
      </v-col>
    </v-row>
    <v-dialog v-model="showPaymentDialog" max-width="600px" persistent>
      <v-card dark class="rounded-lg">
        <v-card-title class="headline grey darken-3">
          Zahlungsart anpassen
          <v-spacer></v-spacer>
          <v-btn icon @click="closePaymentDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <div v-if="loadingPaymentTemplate" class="d-flex justify-center align-center py-10">
            <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
          </div>

          <div v-else>
            <div class="subtitle-1 mb-3 grey--text text--lighten-1">Verfügbare Optionen</div>

            <v-item-group v-model="selectedPaymentIndex" mandatory>
              <v-row dense>
                <v-col v-for="(option, idx) in availablePaymentOptions" :key="idx" cols="12" sm="6">
                  <v-item v-slot="{ active, toggle }">
                    <v-card
                      :color="active ? 'primary' : 'grey darken-3'"
                      class="d-flex align-center pa-3 mb-2 cursor-pointer transition-swing"
                      :class="{ 'elevation-6': active, 'outlined-card': !active }"
                      @click="toggle"
                      flat>
                      <v-scroll-y-transition>
                        <v-icon large class="mr-3" :color="active ? 'white' : 'grey'">
                          {{ active ? "mdi-check-circle" : "mdi-circle-outline" }}
                        </v-icon>
                      </v-scroll-y-transition>
                      <div :class="active ? 'white--text font-weight-bold' : 'grey--text'">
                        {{ option.title }}
                      </div>
                    </v-card>
                  </v-item>
                </v-col>
              </v-row>
            </v-item-group>

            <v-divider class="my-4"></v-divider>

            <div class="subtitle-1 mb-2 grey--text text--lighten-1">Zusatzinformationen</div>
            <v-textarea
              v-model="paymentAdditionalInfo"
              outlined
              dense
              rows="3"
              label="Interne Notiz / Info"
              background-color="grey darken-4"
              hide-details
              class="rounded-lg"></v-textarea>
          </div>
        </v-card-text>

        <v-card-actions class="grey darken-3 pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="closePaymentDialog" :disabled="savingPayment">Abbrechen</v-btn>
          <v-btn color="primary" elevation="0" @click="savePaymentOptions" :loading="savingPayment" :disabled="loadingPaymentTemplate">
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from "../../../api"
import userService from "../../../services/userService"
import deleteDialog from "../../../components/tools/deleteDialog.vue"
import openNutritionSales from "./components/openNutritionSales.vue"

export default {
  components: { deleteDialog, openNutritionSales },
  data() {
    return {
      // Logic / Services
      userService,
      userId: null,
      isShowingAll: false,
      contracts: [],
      // Pagination & Filter State
      viewMode: "open",
      page: 1,
      limit: 20,
      totalPages: 1,
      totalContractsCount: 0,
      searchQuery: "",
      loading: true,
      loadingMore: false,
      debounceTimeout: null,

      serverStats: {
        today: { total: 0, online: 0 },
        yesterday: { total: 0, online: 0 },
      },

      // Sorting
      sortByStartDate: false,

      // Data Arrays
      studios: [],
      studioSelect: [],
      openContracts: [],
      recentlyAcceptedContracts: [],

      // Recently Added View
      recentlyContractsStartDate: this.$moment().subtract(1, "day").startOf("day").format("YYYY-MM-DD"),
      recentlyContractsEndDate: this.$moment().endOf("day").format("YYYY-MM-DD"),
      recentlyContractsLoading: false,
      recentHeaders: [
        { text: "Nr", value: "nr", sortable: false },
        { text: "Datum", value: "latestAcceptedContractCreatedAt" },
        { text: "Studio", value: "studio" },
        { text: "Kategorie", value: "category" },
        { text: "Name", value: "name" },
        { text: "Vertrag", value: "contract" },
        { text: "Aktionen", value: "actions", sortable: false },
      ],

      // UI States
      showNewContractAlert: false,
      contractSauce: null,
      openContractPanel: null,
      userContractPanel: null,
      visibleDeleteDialogIndex: null,
      imageSources: {},

      reasons: [
        "Firmenbestätigung fehlt",
        "Unterschrift gesetzlicher Vertreter fehlt",
        "Geburtsdatum falsch",
        "Falsche Laufzeit",
        "Aktivierungs-Tarif falsch gewählt",
        "Betreibung in der Vergangenheit",
        "Unterschriften unklar",
        "Weitere",
      ],

      // Change Payment
      showPaymentDialog: false,
      loadingPaymentTemplate: false,
      savingPayment: false,
      tempContractTemplate: null, // Speichert die "Blaupause" des Vertrags
      activePaymentEdit: {
        userId: null,
        contractIndex: null,
        studioId: null,
      },
      selectedPaymentIndex: null, // Für die v-item-group
      paymentAdditionalInfo: "",
      availablePaymentOptions: [],
    }
  },

  async created() {
    this.userId = JSON.parse(localStorage.getItem("user")).user._id
    try {
      const [studios, userStudio] = await Promise.all([api.fetchStudios(), api.getUserStudio()])
      this.studios = studios
      if (userStudio && userStudio.studio) {
        this.studioSelect = [userStudio.studio._id]
      }
      this.contracts = await api.fetchContracts()
      this.fetchData(true)
    } catch (e) {
      console.error("Init Error", e)
    }
  },

  computed: {
    sortedOpenContracts() {
      return this.openContracts
    },
    showRecentlyAddedContracts() {
      return this.viewMode === "recent"
    },
    // Berechnung der Gesamtzahl Online (basierend auf geladenen Daten)
    totalOnlineContractsCount() {
      return this.openContracts.filter((u) => this.hasPublicContract(u)).length
    },
    // NEU: Statistik Berechnung für Heute/Gestern
    stats() {
      return this.serverStats
    },
    isApp() {
      return window.flutter_inappwebview || false
    },
  },

  methods: {
    async loadPdfPreview(userId, contractIndex) {
      this.contractSauce = null // Reset, damit man sieht, dass es lädt (optional)
      try {
        // "Vorschau" als Titel sorgt meist dafür, dass kein Wasserzeichen o.ä. drauf ist, je nach Backend
        let blob = await api.getIframeprintUserContract(userId, contractIndex, "Vorschau")
        this.contractSauce = URL.createObjectURL(blob)
      } catch (e) {
        console.error("PDF Load Error", e)
        this.$toast.error("Vorschau konnte nicht geladen werden")
      }
    },
    async openPaymentDialog(user, contract) {
      this.activePaymentEdit = {
        userId: user._id,
        contractIndex: contract.index,
        studioId: contract.studio._id,
      }

      // Aktuelle Werte setzen
      this.paymentAdditionalInfo = contract.paymentOptions?.additionalInfo || ""
      this.showPaymentDialog = true
      this.loadingPaymentTemplate = true

      try {
        // Wir müssen die Vertrags-Vorlage laden, um zu wissen, welche Optionen erlaubt sind
        // (Analog zu userSearch Logic)
        this.tempContractTemplate = await api.fetchContract(contract.contractId)

        // Optionen filtern basierend auf dem Studio des Vertrags
        const rawOptions = this.tempContractTemplate.formFields.find((e) => e.inputType === "paymentOptions")

        if (rawOptions && rawOptions.inputValue) {
          this.availablePaymentOptions = rawOptions.inputValue.filter((e) => e.studios.includes(this.activePaymentEdit.studioId))
        } else {
          this.availablePaymentOptions = []
        }

        // Versuchen den aktuellen Index zu finden
        const currentTitle = contract.paymentOptions?.selection?.title
        if (currentTitle) {
          this.selectedPaymentIndex = this.availablePaymentOptions.findIndex((o) => o.title === currentTitle)
        } else {
          this.selectedPaymentIndex = null
        }
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Laden der Zahlungsoptionen")
        this.closePaymentDialog()
      } finally {
        this.loadingPaymentTemplate = false
      }
    },

    closePaymentDialog() {
      this.showPaymentDialog = false
      this.tempContractTemplate = null
      this.availablePaymentOptions = []
      this.selectedPaymentIndex = null
      this.paymentAdditionalInfo = ""
    },

    async savePaymentOptions() {
      if (this.selectedPaymentIndex === null || this.selectedPaymentIndex === undefined) {
        this.$toast.error("Bitte eine Option auswählen")
        return
      }

      this.savingPayment = true

      const selectedOption = this.availablePaymentOptions[this.selectedPaymentIndex]

      const payload = {
        selection: selectedOption,
        selectedDate: null,
        additionalInfo: this.paymentAdditionalInfo,
      }

      try {
        const status = await api.updatePaymentOptionsContract({
          userId: this.activePaymentEdit.userId,
          index: this.activePaymentEdit.contractIndex,
          paymentOptions: payload,
        })

        if (status && status.nModified) {
          this.$toast.success("Zahlungsart gespeichert")

          // 1. Lokales Daten-Update (für die UI Anzeige im Manager)
          const user = this.openContracts.find((u) => u._id === this.activePaymentEdit.userId)
          if (user) {
            const contract = user.contracts.find((c) => c.index === this.activePaymentEdit.contractIndex)
            if (contract) {
              contract.paymentOptions = payload
            }
          }

          // 2. PDF Reloaden! (DAS IST DER NEUE TEIL)
          // Wir prüfen kurz, ob der Vertrag, den wir gerade bearbeitet haben,
          // auch der ist, der gerade offen ist (sollte er ja sein).
          if (
            this.userContractPanel !== undefined &&
            this.openContracts[this.userContractPanel]._id === this.activePaymentEdit.userId &&
            this.openContractPanel !== undefined
            // Optional: Check ob Contract Index matcht, aber meist reicht User-Match hier
          ) {
            // Wir laden das PDF für genau diesen Vertrag neu
            await this.loadPdfPreview(this.activePaymentEdit.userId, this.activePaymentEdit.contractIndex)
          }

          this.closePaymentDialog()
        } else {
          this.fetchData(true)
          this.closePaymentDialog()
        }
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Speichern")
      } finally {
        this.savingPayment = false
      }
    },
    toggleLoadAll() {
      this.isShowingAll = !this.isShowingAll
      this.fetchData(true)
    },
    async fetchData(reset = false) {
      if (reset) {
        this.page = 1
        this.loading = true
        this.openContracts = []
      } else {
        this.loadingMore = true
      }

      try {
        const params = {
          page: this.page,
          limit: this.limit,
          studios: this.studioSelect,
          search: this.searchQuery,
          sort: this.sortByStartDate ? "start" : "created",
          all: this.isShowingAll,
        }

        const response = await api.fetchContractsByStatus("created", params)

        if (response && response.stats) {
          this.serverStats = response.stats
        }

        let newUsers = []
        if (response && response.users) {
          newUsers = response.users
          this.totalContractsCount = response.total
          this.totalPages = response.pages
        } else if (Array.isArray(response)) {
          newUsers = response
          this.totalContractsCount = newUsers.length
          this.totalPages = 1
        }

        newUsers.forEach((u) => {
          if (u.contracts && u.contracts.length > 0) {
            u.contracts.forEach((c) => {
              c.comment = ""
              c.commentStatus = null
            })
            u.contracts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            u.newestStartDate = u.contracts[0].startDate
          }
        })

        if (reset) {
          this.openContracts = newUsers
        } else {
          this.openContracts = [...this.openContracts, ...newUsers]
        }
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Laden der Verträge")
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    reloadAll() {
      this.fetchData(true)
      this.showNewContractAlert = false
      if (this.showRecentlyAddedContracts) {
        this.fetchRecentlyAcceptedContracts()
      }
    },

    loadMore() {
      if (this.page < this.totalPages) {
        this.page++
        this.fetchData(false)
      }
    },

    debounceSearch() {
      if (this.debounceTimeout) clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(() => {
        this.fetchData(true)
      }, 500)
    },

    async fetchRecentlyAcceptedContracts() {
      if (!this.recentlyContractsStartDate || !this.recentlyContractsEndDate || this.studioSelect.length === 0) {
        this.recentlyAcceptedContracts = []
        return
      }
      this.recentlyContractsLoading = true
      try {
        const params = {
          startDate: this.recentlyContractsStartDate,
          endDate: this.recentlyContractsEndDate,
          studios: this.studioSelect,
        }
        const contracts = await api.fetchRecentlyAcceptedContracts(params)
        this.recentlyAcceptedContracts = contracts
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Laden der Historie.")
      } finally {
        this.recentlyContractsLoading = false
      }
    },

    async takeEditAction(takeAction, index, userId) {
      try {
        await api.updateContractEditMode({
          active: takeAction,
          index,
          user: userId,
        })
        const user = this.openContracts.find((u) => u._id === userId)
        const contract = user.contracts.find((c) => c.index === index)
        if (contract) {
          contract.editingBy = takeAction ? this.userId : null
        }
      } catch (e) {
        this.$toast.error("Status konnte nicht geändert werden")
      }
    },

    addComment(userId, index, comment, commentStatus) {
      if (!comment && !commentStatus) {
        this.$toast.error("Bitte Kommentar oder Grund angeben.")
        return
      }
      api
        .addCommentToContract({ userId, index, comment, commentStatus })
        .then(() => {
          this.$toast.success("Kommentar gespeichert.")
          this.fetchData(true)
        })
        .catch(() => this.$toast.error("Fehler beim Speichern."))
    },

    updateContract(userId, index, comment, commentStatus, status, contractId, studioId, externallySaved, affiliateSaved) {
      if (confirm(status === "rejected" ? "Vertrag zurückweisen?" : "Vertrag bestätigen?")) {
        api
          .updateContractStatus({
            status,
            userId,
            index,
            comment,
            commentStatus,
            externallySaved,
            affiliateSaved,
            sendMailTo: this.getEmailsOfContract(contractId, studioId),
          })
          .then(() => {
            this.$toast.success("Status aktualisiert")
            this.fetchData(true)
          })
          .catch(() => this.$toast.error("Fehler bei Aktualisierung"))
      }
    },

    deleteContract(customerId, contractIndex) {
      api
        .deleteContract({ customerId, contractIndex })
        .then(() => {
          this.visibleDeleteDialogIndex = null
          this.$toast.success("Vertrag gelöscht")
          this.fetchData(true)
        })
        .catch(() => this.$toast.error("Fehler beim Löschen"))
    },

    selectAllStudios() {
      if (this.studioSelect.length === this.studios.length) {
        this.studioSelect = []
      } else {
        this.studioSelect = this.studios.map((s) => s._id)
      }
    },

    getNameOfStudio(studioId) {
      let studio = this.studios.find((e) => e._id === studioId)
      return studio && studio.name ? studio.name : "Unbekannt"
    },

    getStudiosOfContracts(contracts) {
      if (!contracts) return ""
      let studios = contracts.map((e) => e.studio._id)
      studios = studios.map((e) => this.getNameOfStudio(e))
      return [...new Set(studios)].join(", ")
    },

    getNameOfContract(contractId) {
      let contract = this.contracts.find((e) => e._id === contractId)
      return contract?.internalTitle || "Vertrag"
    },

    getEmailsOfContract(contractId, studioId) {
      return []
    },

    hasPublicContract(user) {
      return user.contracts && user.contracts.some((c) => c.publicContract)
    },

    needsTacCopy(contract) {
      const title = contract.category.selection.title || ""
      return title.includes("Neu") || title.includes("Aktionstage") || title.includes("Ehe") || title.includes("OLMA")
    },

    copyToTAC(contract) {
      let http = location.host.includes("local") ? "http://" : "https://"
      window.open(http + location.host + "/dashboard/contract/manager/map/" + contract.customer.selection + "/" + contract.index, "_blank")
    },

    pushToUser(userId) {
      this.$router.push("/dashboard/user/search/" + userId)
    },

    printUserContract(userId, index, title) {
      const safeTitle = (title || "Vertrag").replace(/[\\/:"*?<>|]/g, "_")
      api.printUserContract(userId, index, safeTitle)
    },

    hasImages(contract) {
      return (
        (contract.customer.additionalImage && contract.customer.additionalImage.length > 0) ||
        (contract.customer.additionalImages && contract.customer.additionalImages.length > 0)
      )
    },

    getAllImages(contract) {
      let imgs = []
      if (contract.customer.additionalImage) imgs = [...imgs, ...contract.customer.additionalImage]
      if (contract.customer.additionalImages) {
        imgs = [...imgs, ...contract.customer.additionalImages.map((i) => i.src)]
      }
      return imgs
    },

    downloadContractImageWrapper(imgSrc) {
      api.downloadContractImageData(imgSrc, "Anhang")
    },
  },

  watch: {
    studioSelect(val) {
      if (this.viewMode === "open" && val.length > 0) {
        this.fetchData(true)
      } else if (this.viewMode === "recent" && val.length > 0) {
        this.fetchRecentlyAcceptedContracts()
      } else {
        this.fetchData(true)
      }
    },
    viewMode(val) {
      if (val === "open") {
        if (this.openContracts.length === 0) this.fetchData(true)
      } else {
        this.fetchRecentlyAcceptedContracts()
      }
    },
    recentlyContractsStartDate() {
      this.fetchRecentlyAcceptedContracts()
    },
    recentlyContractsEndDate() {
      this.fetchRecentlyAcceptedContracts()
    },
    async openContractPanel(val) {
      if (val !== undefined && this.userContractPanel !== undefined) {
        let user = this.openContracts[this.userContractPanel]
        // Sicherheitscheck, falls Indizes verrutschen
        if (user && user.contracts && user.contracts[val]) {
          let contract = user.contracts[val]
          await this.loadPdfPreview(user._id, contract.index)
        }
      } else {
        this.contractSauce = null
      }
    },
    userContractPanel() {
      this.openContractPanel = undefined
      this.contractSauce = null
    },
    sortByStartDate() {
      this.fetchData(true)
    },
  },
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.transition-swing {
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}
.outlined-card {
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
}
.transparent {
  background-color: transparent !important;
}
.border-top {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.border-white {
  border: 1px solid rgba(255, 255, 255, 0.5);
}
</style>
